import { type FC, useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { F0Box, F0Button, F0Icon, F0Text } from '@factorialco/f0-react'

import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  Marketplace,
} from '@factorialco/f0-react/icons/app'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import { useItManagementFeatureAvailability } from 'modules/it_management/hooks/useItManagementFeatureAvailability'
import i18n from 'modules/core/lib/i18n'
import { useDeviceCatalogCartContext } from 'modules/it_management/contexts/DeviceCatalogCartContext'
import { useDeviceCatalogCheckout } from 'modules/it_management/hooks/device_catalog/useDeviceCatalogCheckout'
import { navigation } from 'modules/it_management/lib/deviceCatalogNavigation'

import { CartItemRow } from 'modules/it_management/components/device_catalog/MarketplaceCartSidepanel/CartItemRow'

const STEP_IDS = [
  'cart_items',
  'device_configuration',
  'employee_assignment',
  'device_provisioning',
  'shipping_configuration',
  'payment_routing',
  'order_summary',
] as const

const TOTAL_STEPS = STEP_IDS.length

function formatEuroPrice(amount: number): string {
  return `${amount.toLocaleString('de-DE', { minimumFractionDigits: 0 })} €`
}

const OrderPlanPage: FC = () => {
  const navigate = useNavigate()
  const cart = useDeviceCatalogCartContext()
  const [openSteps, setOpenSteps] = useState<Set<number>>(new Set([0]))
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set())

  const orderPlanSubmittedPath = `/device-catalog/${navigation.children.orderPlanSubmitted.path}`

  const { handleCheckout, isCheckingOut } = useDeviceCatalogCheckout(cart, {
    onSuccess: () => navigate(orderPlanSubmittedPath),
  })

  const {
    isItManagementEnabled,
    loading: featureLoading,
    error: featureError,
  } = useItManagementFeatureAvailability()

  const deviceCount = cart.itemCount
  const totalFormatted = formatEuroPrice(cart.total)

  const stepLabels = useMemo(
    () => [
      {
        title: i18n.t('it_management.device_catalog.order_plan.steps.cart_items'),
        itemsBadge: i18n.t('it_management.device_catalog.order_plan.steps.cart_items_badge', {
          count: deviceCount,
        }),
        detail: i18n.t('it_management.device_catalog.order_plan.steps.cart_items_detail', {
          count: deviceCount,
        }),
        summary: i18n.t('it_management.device_catalog.order_plan.steps.cart_items_summary', {
          count: deviceCount,
          total: totalFormatted,
        }),
      },
      {
        title: i18n.t('it_management.device_catalog.order_plan.steps.device_configuration'),
        detail: i18n.t(
          'it_management.device_catalog.order_plan.steps.device_configuration_description'
        ),
      },
      {
        title: i18n.t('it_management.device_catalog.order_plan.steps.employee_assignment'),
        detail: i18n.t(
          'it_management.device_catalog.order_plan.steps.employee_assignment_description'
        ),
      },
      {
        title: i18n.t('it_management.device_catalog.order_plan.steps.device_provisioning'),
        detail: i18n.t(
          'it_management.device_catalog.order_plan.steps.device_provisioning_description'
        ),
      },
      {
        title: i18n.t('it_management.device_catalog.order_plan.steps.shipping_configuration'),
        detail: i18n.t(
          'it_management.device_catalog.order_plan.steps.shipping_configuration_detail'
        ),
      },
      {
        title: i18n.t('it_management.device_catalog.order_plan.steps.payment_routing'),
        detail: i18n.t('it_management.device_catalog.order_plan.steps.payment_routing_detail'),
      },
      {
        title: i18n.t('it_management.device_catalog.order_plan.steps.order_summary'),
        detail: i18n.t('it_management.device_catalog.order_plan.steps.order_summary_detail', {
          total: totalFormatted,
        }),
      },
    ],
    [deviceCount, totalFormatted]
  )

  const handleContinue = useCallback((stepIndex: number) => {
    setCompletedSteps((prev) => new Set([...prev, stepIndex]))
    setOpenSteps((prev) => {
      const next = new Set(prev)
      next.delete(stepIndex)
      if (stepIndex < TOTAL_STEPS - 1) {
        next.add(stepIndex + 1)
      }
      return next
    })
  }, [])

  const handlePlaceOrder = useCallback(() => {
    handleCheckout()
  }, [handleCheckout])

  const handleStepClick = useCallback((index: number) => {
    setOpenSteps((prev) => {
      const next = new Set(prev)
      if (next.has(index)) {
        next.delete(index)
      } else {
        next.add(index)
      }
      return next
    })
  }, [])

  if (cart.items.length === 0) {
    navigate('/device-catalog', { replace: true })
    return null
  }

  return (
    <ResponsivePage.Body
      authorized={isItManagementEnabled}
      error={featureError}
      loading={featureLoading}
    >
      <div style={{ maxWidth: 830 }}>
        <F0Box display="flex" flexDirection="column" padding="xl" gap="lg">
          <div style={{ alignSelf: 'start', width: 150 }}>
            <F0Button
            variant="ghost"
            label={i18n.t('it_management.device_catalog.order_plan.back_to_catalog')}
            icon={ChevronLeft}
            iconPosition="start"
            onClick={() => navigate('/device-catalog')}
          />
          </div>
        <F0Box display="flex" flexDirection="column" gap="xs">
          <F0Text
            variant="label"
            content={i18n.t('it_management.device_catalog.order_plan.title')}
          />
          <F0Text
            variant="description"
            content={i18n.t('it_management.device_catalog.order_plan.subtitle', {
              count: deviceCount,
            })}
          />
        </F0Box>

        <F0Box display="flex" flexDirection="column" gap="md">
          {STEP_IDS.map((stepId, index) => {
            const isOpen = openSteps.has(index)
            const isDone = completedSteps.has(index)
            const isLast = index === TOTAL_STEPS - 1
            const labels = stepLabels[index]
            const isCartStep = index === 0
            const detail =
              labels == null ? undefined : index === 6 ? labels.detail : isCartStep || index === 3 ? undefined : labels.detail

            return (
              <F0Box
                key={stepId}
                border="default"
                borderColor="secondary"
                borderRadius="md"
                overflow="hidden"
              >
                <button
                  type="button"
                  onClick={() => handleStepClick(index)}
                  className="flex w-full flex-row items-center gap-3 px-4 py-4 text-left hover:bg-f1-bg-secondary/50"
                  aria-expanded={isOpen}
                  aria-controls={`order-plan-step-${index}`}
                  id={`order-plan-step-heading-${index}`}
                >
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center" aria-hidden>
                    {isDone ? (
                      <F0Icon icon={Check} size="md" color="default" />
                    ) : (
                      <span className="text-sm font-medium text-f1-text-bold">{index + 1}</span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1 flex flex-col gap-0.5">
                    <div className="flex flex-row items-center gap-2 flex-wrap">
                      <F0Text variant="label" content={labels?.title ?? ''} />
                      {labels != null && isCartStep && 'itemsBadge' in labels && (
                        <span className="rounded-full bg-f1-bg-secondary px-2 py-0.5 text-xs text-f1-text-secondary">
                          {labels.itemsBadge}
                        </span>
                      )}
                    </div>
                    {detail != null && <F0Text variant="description" content={detail} />}
                  </div>
                  <span className="flex shrink-0" aria-hidden>
                    <F0Icon icon={isOpen ? ChevronUp : ChevronDown} size="sm" color="default" />
                  </span>
                </button>

                {isOpen && (
                  <F0Box
                    id={`order-plan-step-${index}`}
                    role="region"
                    aria-labelledby={`order-plan-step-heading-${index}`}
                    padding="lg"
                    borderTop="default"
                    borderColor="secondary"
                    display="flex"
                    flexDirection="column"
                    gap="lg"
                    {...(index > 0 && { 'data-order-plan-step-content': true })}
                  >
                    {index === 0 && (
                      <F0Box display="flex" flexDirection="column" gap="md">
                        {cart.items.map((item) => (
                          <CartItemRow
                            key={`${item.variantId}-${item.offerId}`}
                            item={item}
                            onUpdateQuantity={cart.updateQuantity}
                            onRemove={cart.removeItem}
                            variant="checkout"
                          />
                        ))}
                      </F0Box>
                    )}
                    {index === 3 && (
                      <F0Box background="tertiary" borderRadius="md" padding="md">
                        <F0Text
                          variant="description"
                          content={i18n.t(
                            'it_management.device_catalog.order_plan.steps.device_provisioning_no_mdm_message'
                          )}
                        />
                      </F0Box>
                    )}
                    {index > 0 && index < 6 && index !== 3 && labels != null && (
                      <F0Box background="tertiary" borderRadius="md" padding="md">
                        <F0Text variant="description" content={labels.detail} />
                      </F0Box>
                    )}
                    {index === 6 && (
                      <F0Box background="tertiary" borderRadius="md" padding="md">
                        <F0Text
                          variant="body"
                          content={i18n.t(
                            'it_management.device_catalog.order_plan.steps.order_summary_detail',
                            { total: totalFormatted }
                          )}
                        />
                      </F0Box>
                    )}

                    <F0Box data-order-plan-step-actions data-it-management-primary-action>
                      <F0Box display="flex" justifyContent="end">
                        {isLast ? (
                          <F0Button
                            label={i18n.t('it_management.device_catalog.order_plan.place_order')}
                            variant="default"
                            size="md"
                            icon={Marketplace}
                            onClick={handlePlaceOrder}
                            disabled={isCheckingOut}
                          />
                        ) : (
                          <F0Button
                            label={i18n.t(
                              'it_management.device_catalog.order_plan.continue_with_arrow'
                            )}
                            variant="default"
                            size="md"
                            onClick={() => handleContinue(index)}
                          />
                        )}
                      </F0Box>
                    </F0Box>
                  </F0Box>
                )}
              </F0Box>
            )
          })}
        </F0Box>
        </F0Box>
      </div>
    </ResponsivePage.Body>
  )
}

export default OrderPlanPage
